import { useState, useRef, useEffect } from "react";
import { IMAGE_CATEGORY_LABELS, type ImageCategory, type LibraryImage } from "../utils/imageLibraryStore";
import { reportError } from "../../../api/errors";
import {
  createPlatformLibraryImage,
  deletePlatformLibraryImage,
  fetchPlatformLibraryImages,
  rehostPlatformLibraryImageFromUrl,
  updatePlatformLibraryImage,
  uploadPlatformImageToOss,
} from "../../../api/platformLibrary";

const CATEGORY_ICONS: Record<ImageCategory | "all", string> = {
  all: "ri-image-2-line",
  activity: "ri-football-line",
  vote: "ri-checkbox-circle-line",
  landing: "ri-layout-masonry-line",
  general: "ri-folder-image-line",
  news: "ri-newspaper-line",
};

type CategoryFilter = ImageCategory | "all";

export default function PlatformImageLibrary() {
  const [images, setImages] = useState<LibraryImage[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>("all");
  const [search, setSearch] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<LibraryImage | null>(null);
  const [editImage, setEditImage] = useState<LibraryImage | null>(null);

  const [uploadForm, setUploadForm] = useState<{ name: string; category: ImageCategory; url: string }>({
    name: "",
    category: "general",
    url: "",
  });
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [rehostingLegacyId, setRehostingLegacyId] = useState<string | null>(null);

  useEffect(() => {
    if (!pendingFile) {
      setFilePreviewUrl(null);
      return;
    }
    const u = URL.createObjectURL(pendingFile);
    setFilePreviewUrl(u);
    return () => {
      URL.revokeObjectURL(u);
    };
  }, [pendingFile]);

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    void fetchPlatformLibraryImages()
      .then((rows) => {
        if (!cancelled) setImages(rows);
      })
      .catch((e) => {
        if (!cancelled) reportError(e, "加载资源库失败");
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categories: Array<{ key: CategoryFilter; label: string }> = [
    { key: "all", label: "全部图片" },
    ...Object.entries(IMAGE_CATEGORY_LABELS).map(([key, label]) => ({ key: key as CategoryFilter, label })),
  ];

  const filtered = images.filter((img) => {
    const matchCat = activeCategory === "all" || img.category === activeCategory;
    const matchSearch = !search.trim() || (img.name ?? "").includes(search.trim());
    return matchCat && matchSearch;
  });

  const countByCategory = (cat: ImageCategory) => images.filter((img) => img.category === cat).length;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setUploadForm((p) => ({ ...p, url: "" }));
  };

  const handleUploadSubmit = async () => {
    if (!uploadForm.name.trim()) return;
    let finalUrl = uploadForm.url.trim();
    try {
      if (pendingFile) finalUrl = await uploadPlatformImageToOss(pendingFile);
      if (!finalUrl) return;
      const created = await createPlatformLibraryImage({
        name: uploadForm.name.trim(),
        category: uploadForm.category,
        url: finalUrl,
      });
      setImages((prev) => [created, ...prev]);
      setUploadForm({ name: "", category: "general", url: "" });
      setPendingFile(null);
      setUploadModalOpen(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e) {
      reportError(e, "上传失败");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deletePlatformLibraryImage(id);
      setImages((prev) => prev.filter((img) => img.id !== id));
      setDeleteConfirmId(null);
    } catch (e) {
      reportError(e, "删除失败");
    }
  };

  const handleRehostFromCurrentUrl = async (legacyId: string) => {
    setRehostingLegacyId(legacyId);
    try {
      const { image: updated } = await rehostPlatformLibraryImageFromUrl(legacyId);
      setImages((prev) => prev.map((img) => (img.id === updated.id ? updated : img)));
    } catch (err) {
      reportError(err, "按链接转存 OSS 失败");
    } finally {
      setRehostingLegacyId(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!editImage) return;
    try {
      const updated = await updatePlatformLibraryImage(editImage.id, { name: editImage.name, category: editImage.category });
      setImages((prev) => prev.map((img) => (img.id === updated.id ? updated : img)));
      setEditImage(null);
    } catch (e) {
      reportError(e, "保存失败");
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-gray-800">图片资源库</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {listLoading ? "正在加载资源库…" : `统一管理各租户可引用的公共图片素材，共 ${images.length} 张`}
          </p>
        </div>
        <button
          onClick={() => setUploadModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-xl cursor-pointer hover:bg-amber-400 transition-colors whitespace-nowrap"
        >
          <i className="ri-upload-cloud-line"></i> 上传图片
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-5 flex gap-3">
        <i className="ri-information-line text-amber-500 flex-shrink-0 mt-0.5"></i>
        <div className="text-xs text-amber-700 space-y-1">
          <p className="font-medium">资源库使用说明</p>
          <p>• 此处上传的图片将在各租户管理端的「活动创建」「投票创建」「落地页管理」中作为「从资源库选择」的选项</p>
          <p>• 请按分类上传，方便租户快速筛选；建议图片宽度不低于 800px，落地页背景建议竖版（393×420）</p>
          <p>• 删除图片后已引用该图片的页面不受影响，但后续无法再从库中选择</p>
          <p>• 卡片上「转存」按当前条目里的图片链接由服务端下载并写入 OSS（已在 OSS 的条目无需操作）</p>
        </div>
      </div>

      <div className="flex gap-5">
        {/* Sidebar categories */}
        <div className="w-48 flex-shrink-0">
          <div className="bg-white rounded-2xl border border-gray-100 p-3 space-y-1">
            {categories.map((cat) => {
              const count = cat.key === "all" ? images.length : countByCategory(cat.key as ImageCategory);
              return (
                <button
                  key={cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs cursor-pointer transition-all whitespace-nowrap ${
                    activeCategory === cat.key
                      ? "bg-amber-500 text-white font-medium"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <i className={`${CATEGORY_ICONS[cat.key]} text-base`}></i>
                    {cat.label}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${activeCategory === cat.key ? "bg-amber-400 text-white" : "bg-gray-100 text-gray-500"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Search + stats bar */}
          <div className="bg-white rounded-2xl border border-gray-100 px-4 py-3 mb-4 flex items-center gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜索图片名称..."
                className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-amber-400"
              />
            </div>
            <span className="text-xs text-gray-400 whitespace-nowrap">显示 {filtered.length} / {images.length} 张</span>
          </div>

          {/* Image grid */}
          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 flex flex-col items-center justify-center py-20">
              <i className="ri-image-line text-5xl text-gray-200 mb-3"></i>
              <p className="text-sm text-gray-400 text-center px-6">
                {images.length === 0
                  ? "资源库中还没有图片条目（若已上传 OSS，请确认「上传到资源库」是否保存成功）。"
                  : "当前侧边分类或搜索条件下没有图片，可点左侧「全部图片」或清空搜索。"}
              </p>
              <button onClick={() => setUploadModalOpen(true)} className="mt-4 text-xs text-amber-600 cursor-pointer hover:underline">
                立即上传第一张图片
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {filtered.map((img) => (
                <div key={img.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden group">
                  <div
                    className="relative cursor-pointer"
                    style={{ aspectRatio: "16/9" }}
                    onClick={() => setPreviewImage(img)}
                  >
                    <img
                      src={img.url}
                      alt={img.name}
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover object-top"
                    />
                    {rehostingLegacyId === img.id && (
                      <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white text-xs gap-1">
                        <i className="ri-loader-4-line text-xl animate-spin"></i>
                        <span>正在转存…</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center pointer-events-none">
                      <i className="ri-zoom-in-line text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
                    </div>
                    {img.isLocal && (
                      <div className="absolute top-2 left-2 bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                        本地
                      </div>
                    )}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button
                        type="button"
                        title={img.isLocal ? "已在 OSS" : "按当前链接转存到 OSS"}
                        disabled={rehostingLegacyId !== null || img.isLocal === true}
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleRehostFromCurrentUrl(img.id);
                        }}
                        className="w-7 h-7 bg-white rounded-lg flex items-center justify-center text-gray-600 hover:text-emerald-600 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <i className="ri-cloud-upload-line text-xs"></i>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setEditImage({ ...img }); }}
                        className="w-7 h-7 bg-white rounded-lg flex items-center justify-center text-gray-600 hover:text-amber-600 cursor-pointer"
                      >
                        <i className="ri-edit-line text-xs"></i>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(img.id); }}
                        className="w-7 h-7 bg-white rounded-lg flex items-center justify-center text-gray-600 hover:text-red-600 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    </div>
                  </div>
                  <div className="px-3 py-2.5">
                    <p className="text-xs font-semibold text-gray-800 truncate">{img.name}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full font-medium">
                        {IMAGE_CATEGORY_LABELS[img.category] ?? img.category}
                      </span>
                      <span className="text-[10px] text-gray-400">{img.uploadedAt}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-[520px] p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-upload-cloud-2-line text-amber-500 text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800">上传图片到资源库</h3>
                <p className="text-xs text-gray-400">本地上传将转存阿里云 OSS；也可直接粘贴 https 图片链接</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setUploadModalOpen(false);
                  setPendingFile(null);
                  setUploadForm({ name: "", category: "general", url: "" });
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                className="ml-auto w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="space-y-4">
              {/* Image picker */}
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-2">选择图片</label>
                {filePreviewUrl ? (
                  <div className="relative rounded-xl overflow-hidden border border-gray-200 mb-2" style={{ height: 140 }}>
                    <img src={filePreviewUrl} alt="preview" referrerPolicy="no-referrer" className="w-full h-full object-cover object-top" />
                    <button
                      type="button"
                      onClick={() => {
                        setPendingFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      className="absolute top-2 right-2 w-7 h-7 bg-white rounded-lg flex items-center justify-center text-gray-600 hover:text-red-600 cursor-pointer"
                    >
                      <i className="ri-close-line text-xs"></i>
                    </button>
                    <span className="absolute top-2 left-2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full">上传到 OSS</span>
                  </div>
                ) : uploadForm.url ? (
                  <div className="relative rounded-xl overflow-hidden border border-gray-200 mb-2" style={{ height: 140 }}>
                    <img src={uploadForm.url} alt="preview" referrerPolicy="no-referrer" className="w-full h-full object-cover object-top" />
                    <span className="absolute top-2 left-2 bg-teal-500 text-white text-[10px] px-2 py-0.5 rounded-full">链接预览</span>
                  </div>
                ) : null}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-2 border border-dashed border-gray-300 rounded-xl text-xs text-gray-500 hover:border-amber-400 hover:text-amber-600 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-folder-image-line"></i> 本地上传
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                  <input
                    type="url"
                    value={pendingFile ? "" : uploadForm.url}
                    onChange={(e) => {
                      setUploadForm((p) => ({ ...p, url: e.target.value }));
                      setPendingFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    placeholder="或粘贴图片链接 https://..."
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-amber-400"
                  />
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">图片名称 *</label>
                <input
                  type="text"
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="如：2025赛季主场观赛封面"
                  maxLength={30}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
                />
              </div>

              {/* Category */}
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">图片分类 *</label>
                <div className="grid grid-cols-4 gap-2">
                  {(Object.entries(IMAGE_CATEGORY_LABELS) as [ImageCategory, string][]).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setUploadForm((p) => ({ ...p, category: key }))}
                      className={`py-2.5 text-xs rounded-xl cursor-pointer transition-all whitespace-nowrap border ${
                        uploadForm.category === key
                          ? "bg-amber-500 text-white border-amber-500 font-medium"
                          : "border-gray-200 text-gray-600 hover:border-amber-300"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                type="button"
                onClick={() => {
                  setUploadModalOpen(false);
                  setPendingFile(null);
                  setUploadForm({ name: "", category: "general", url: "" });
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => void handleUploadSubmit()}
                disabled={!uploadForm.name.trim() || (!pendingFile && !uploadForm.url.trim())}
                className="flex-1 py-2.5 text-sm text-white bg-amber-500 rounded-xl hover:bg-amber-400 cursor-pointer disabled:opacity-40 whitespace-nowrap"
              >
                上传到资源库
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-8" onClick={() => setPreviewImage(null)}>
          <div className="max-w-4xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={previewImage.url}
              alt={previewImage.name}
              referrerPolicy="no-referrer"
              className="max-w-full max-h-[70vh] rounded-xl object-contain"
            />
            <div className="text-center mt-4">
              <p className="text-white font-medium">{previewImage.name}</p>
              <p className="text-white/50 text-sm">
                {IMAGE_CATEGORY_LABELS[previewImage.category] ?? previewImage.category} · {previewImage.uploadedAt}
              </p>
              <button onClick={() => setPreviewImage(null)} className="mt-3 text-white/60 hover:text-white text-sm cursor-pointer">关闭预览</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editImage && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-96">
            <h3 className="font-bold text-gray-800 mb-4">编辑图片信息</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">图片名称</label>
                <input
                  type="text"
                  value={editImage.name}
                  onChange={(e) => setEditImage((p) => p ? { ...p, name: e.target.value } : null)}
                  maxLength={30}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">图片分类</label>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.entries(IMAGE_CATEGORY_LABELS) as [ImageCategory, string][]).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setEditImage((p) => p ? { ...p, category: key } : null)}
                      className={`py-2 text-xs rounded-xl cursor-pointer transition-all border ${editImage.category === key ? "bg-amber-500 text-white border-amber-500" : "border-gray-200 text-gray-600 hover:border-amber-300"}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setEditImage(null)} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => void handleSaveEdit()} className="flex-1 py-2.5 text-sm text-white bg-amber-500 rounded-xl hover:bg-amber-400 cursor-pointer whitespace-nowrap">保存</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-80">
            <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-gray-800 text-center mb-2">确认删除此图片？</h3>
            <p className="text-sm text-gray-400 text-center mb-5 leading-relaxed">删除后无法恢复，但已引用该图片的页面不受影响。</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => void handleDelete(deleteConfirmId)} className="flex-1 py-2.5 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
